import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import uuid from '../../utils/uuidv4'

/**
 * The LabeledContentSection is used to add a label to a section of content.
 * Please note that a frame around the content is added for VW6.
 * Note: Please do not mix the LabeledContentSection with the `.o-content-section` helper.
 * The spacing of `.o-content-section` is added to the LabeledContentSection by default.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-labeled-content-section.html
 *
 * @param {string} title - The section title.
 * @param {string} [id] - Optional identifier, will be autogenerated when none is passed.
 * @param {string} [className] - Additional class on the component element.
 * @param {React.ReactNode} [children] - The content section children.
 * @param {string} [testId] - `[data-testid]` attribute on the component element.
 * @param {Object} [otherProps] - Other props to be passed to the component element.
 * @return {JSX.Element} - The {@link LabeledContentSection} element.
 * @constructor
 */
export function LabeledContentSection({ className, children, id, title, testId, ...otherProps }) {
  const classNameList = classNames('c-labeled-content-section', className).trim()
  /**
   * Internal identifier to improve a11y for the component.
   * @type {string}
   */
  const labeledContentSectionId = `c-labeled-content-section-${id ?? uuid()}`

  return (
    <div className={classNameList} {...otherProps}>
      <div className="c-labeled-content-section__label">
        <span className="c-labeled-content-section__label-text" id={labeledContentSectionId}>
          {title}
        </span>
      </div>
      <div className="c-labeled-content-section__content" aria-labelledby={labeledContentSectionId}>
        {children}
      </div>
    </div>
  )
}

LabeledContentSection.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string, // Bronson template: 'id'.
  title: PropTypes.string.isRequired, // Bronson template: 'labeled-content-section-title'.
  children: PropTypes.node, // Bronson template: 'labeled-content-section-content'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}
